export const GLASS = {
  blur: { sm: 'blur(8px)', md: 'blur(12px)', lg: 'blur(16px)' },
  saturate: 'saturate(180%)',
  backdrop: {
    sm: 'saturate(180%) blur(8px)',
    md: 'saturate(180%) blur(12px)',
    lg: 'saturate(180%) blur(16px)',
  },
  bg: {
    dark: 'rgba(15,23,42,0.75)',
    mid: 'rgba(30,41,59,0.8)',
    light: 'rgba(255,255,255,0.75)',
    subtle: 'rgba(255,255,255,0.04)',
    subtleHover: 'rgba(255,255,255,0.07)',
  },
  border: {
    dark: 'rgba(255,255,255,0.08)',
    darkStrong: 'rgba(255,255,255,0.15)',
    light: 'rgba(0,0,0,0.08)',
  },
  shadow: {
    card: '0 8px 32px rgba(0,0,0,0.4)',
    cardLight: '0 8px 24px rgba(0,0,0,0.12)',
  },
  /**
   * PX.2 T.0 — Universal-shell layout tokens. Single source of truth for the
   * fixed-height floating header + the content-region offset + the z-index
   * stack. Replaces the scattered magic numbers:
   *   - `HEADER_H = 55`  in `ui/demo/UISandboxPage.tsx`
   *   - `top: 68`        in `esri/components/EsriMapView.tsx`
   *
   * `headerH` is mirrored as the `--header-h` CSS custom property in
   * `globals.css` so both JS (`sx`/inline-style) and CSS consumers read ONE
   * value. The number below is the *current effective* header reservation
   * (the floating GlassHeader overlay sits at `top: 12` + ~32px control row +
   * a small gap below the avatar); T.1–T.3 swap their hardcoded constants to
   * this token with zero visual diff.
   *
   * The z-index scale is ascending: map < panel < header < avatar < menu <
   * dialog. Anything floating over the map references it instead of a literal
   * so the stacking contract is auditable in one place.
   */
  LAYOUT: {
    /** Header-row height / content-region top offset, in px. == `--header-h`. */
    headerH: 55,
    /**
     * Map padding reservation under the floating header (slightly less than
     * `headerH` because the map's glass header overlaps the very top edge).
     * Replaces the literal `top: 68` in EsriMapView — kept as a sibling token
     * so the two reservations can be tuned independently if needed.
     */
    mapTopPad: 68,
    /** Ascending z-index stack for everything that floats over the map. */
    zIndex: {
      map: 0,
      panel: 10,
      header: 15,
      avatar: 20,
      menu: 1300,
      dialog: 1400,
    },
    /**
     * Viewport-pinned avatar slot. The avatar/user cluster anchors a constant
     * distance from the viewport's right edge — NEVER a function of panel
     * width (that coupling is the PX.2 defect-1b avatar-overflow bug).
     *
     * `right: 84` preserves the avatar's pre-PX.2 resting position: the old
     * header box rested at `right: 84` (panel closed) and the avatar cluster
     * sat at its right inner edge, so pinning at 84 is a zero-visual-diff
     * decoupling — the avatar stops moving without jumping. `top: 12` matches
     * the old header box's `top: 12`. The cluster's own ~8px inner pad is kept
     * on the GlassHeader avatar Box so the content right edge is unchanged.
     */
    avatarSlot: {
      top: 12,
      right: 84,
    },
    /**
     * Side-panel offset below the floating header. The content region sits
     * below the header by construction; this token is the shared value any
     * surface that still needs an explicit reservation reads (== headerH).
     */
    panelOffset: 55,
  },
} as const;

/**
 * PX.2 T.0 — convenience alias for the layout sub-namespace so consumers can
 * `import { LAYOUT } from '@/lib/glassTokens'` without reaching through GLASS.
 */
export const LAYOUT = GLASS.LAYOUT;
